import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Avatar,
  Skeleton,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ApiHelper } from '../utils/apiHelper';

const statusColors = {
  WAITING_SENT: 'warning',
  COMPLETED: 'success',
  REJECTED: 'error',
  PENDING: 'info',
};

const statusLabels = {
  WAITING_SENT: 'Gönderim Bekleniyor',
  COMPLETED: 'Tamamlandı',
  REJECTED: 'Reddedildi',
  PENDING: 'Beklemede',
  REFUNDED: 'İade Edildi',
  WAITING_REFUND: 'İade Bekleniyor',
};

export default function InvestmentDetailPage() {
  const [investmentDetail, setInvestmentDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    const fetchInvestmentDetail = async () => {
      try {
        const response = await ApiHelper.get(`/admin/investment-details/${id}`);
        if (response.data.code === '00000') {
          setInvestmentDetail(response.data.response);
        } else {
          setError('Yatırım detayları alınamadı');
        }
      } catch (err) {
        setError('Bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchInvestmentDetail();
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Container>
    );
  }

  const {
    investment,
    startupName,
    startupMainLogoPath,
    userName,
    title,
    isPreFunding
  } = investmentDetail;

  const displayName = title || userName;
  const displayStatus = isPreFunding ? 'Ön Yatırım' : statusLabels[investment.status] || investment.status;
  const displayStatusColor = isPreFunding ? 'info' : statusColors[investment.status] || 'default';
  const displayPaymentType = isPreFunding ? '-' : (investment.paymentType === 'BANK_TRANSFER' ? 'Banka Transferi' : investment.paymentType);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={`/${startupMainLogoPath}`}
            alt={startupName}
            sx={{ width: 64, height: 64 }}
          />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Yatırım Detayı
            </Typography>
            <Chip
              label={displayStatus}
              color={displayStatusColor}
              sx={{ fontWeight: 'medium' }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Startup
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {startupName}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Yatırımcı
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {displayName}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MoneyIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Yatırım Tutarı
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {Number(investment.amount).toLocaleString('tr-TR')} TL
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BankIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Ödeme Yöntemi
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {displayPaymentType}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TimeIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Oluşturulma Tarihi
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {format(new Date(investment.createdAt), 'dd.MM.yyyy HH:mm')}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TimeIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Son Güncelleme
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {format(new Date(investment.updatedAt), 'dd.MM.yyyy HH:mm')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Payment Details */}
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>
            Ödeme Detayları
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isPreFunding ? 'Bu bir ön taleptir' : `Referans Kodu: ${investment.paymentDetail}`}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}